import { Flex } from 'antd';
import React, { useRef } from 'react';
import Draggable from 'react-draggable';
import { useSelector } from 'react-redux';

import { CELL_HEIGHT, WIDTH_CARD_TASK_SMALL } from '../const';
import useCommitTask from '../hooks/useCommitTask';
import { RootState } from '../store';
import { DayWeekType, ItemTaskType, TaskPositionType } from '../types';
import CalendarTaskBodyCaption from './calendarTaskBodyCaption';
import CalendarTaskBodyForthLine from './calendarTaskBodyForthLine';
import CalendarTaskBodySecondLine from './calendarTaskBodySecondLine';
import CalendarTaskBodyThirdLine from './calendarTaskBodyThirdLine';

interface ICalendarTaskBody {
    task: TaskPositionType;
    cellWidth: number;
    handleDrag: (e: any, data: any, taskId: string) => void;
    saveDragResult: (taskId: string) => void;
    typeDayWeek: DayWeekType;
}

const CalendarTaskBody: React.FC<ICalendarTaskBody> = ({
    task,
    cellWidth,
    typeDayWeek,
    handleDrag,
    saveDragResult,
}) => {
    const isDragging = useSelector<RootState, boolean>((state) => state.calendar.isDragging);
    const currentIdItem = useSelector<RootState, string>((state) => state.calendar.currentIdItem);
    const currentTask = useSelector<RootState, ItemTaskType | null>((state) => state.calendar.currentTask);
    const { switchCurrentTask } = useCommitTask(typeDayWeek);
    const taskRef = useRef<HTMLDivElement>(null);
    const justOneClickController = useRef<boolean>(false);

    const currentBorder = currentTask?.id === task.id ? `1px solid ${task.color}` : 'none';

    const widthTaskBody = cellWidth / task.colCount - task.marginLeft - 2;

    return (
        <Draggable
            key={`task-${task.id}`}
            bounds={'parent'}
            grid={[cellWidth, CELL_HEIGHT]}
            position={{
                x: task.col * cellWidth,
                y: task.row * CELL_HEIGHT,
            }}
            onDrag={(e, data) => handleDrag(e, data, task.id)}
            onStop={() => saveDragResult(task.id)}
            nodeRef={taskRef}
        >
            <div
                ref={taskRef}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isDragging && !justOneClickController.current) {
                        justOneClickController.current = true;
                        switchCurrentTask(task);
                        setTimeout(() => {
                            justOneClickController.current = false;
                        }, 500);
                    }
                }}
                style={{
                    height: task.height * CELL_HEIGHT,
                    width: widthTaskBody,
                    left: task.marginLeft + (task.colShow - 1) * (cellWidth / task.colCount),
                    backgroundColor: 'white',
                    borderRadius: 5,
                    padding: `${task.height > 1 ? 3 : 1}px ${widthTaskBody > WIDTH_CARD_TASK_SMALL ? 10 : 4}px`,
                    position: 'absolute',
                    borderLeft: `7px solid ${task.color}`,
                    borderRight: currentBorder,
                    borderTop: currentBorder,
                    borderBottom: currentBorder,
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    boxShadow:
                        currentTask?.id === task.id
                            ? '0px 0px 6px rgba(100, 100, 100, 0.6)'
                            : '0px 0px 6px rgba(0, 0, 0, 0.2)',
                    overflow: 'hidden',
                    zIndex: currentTask?.id === task.id ? task.zIndex + 100 : task.zIndex,
                    opacity: task.schedule_item_id === currentIdItem || !currentIdItem ? 1 : 0.3,
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        backgroundColor: task.color,
                        opacity: 0.25,
                        top: 0,
                        bottom: 0,
                        left: 0,
                        right: 0,
                    }}
                ></div>
                <Flex vertical style={{ zIndex: 1001 }}>
                    <CalendarTaskBodyCaption task={task} />
                    <CalendarTaskBodySecondLine task={task} widthTaskBody={widthTaskBody} />
                    <CalendarTaskBodyThirdLine task={task} widthTaskBody={widthTaskBody} />
                    <CalendarTaskBodyForthLine task={task} widthTaskBody={widthTaskBody} />
                </Flex>
            </div>
        </Draggable>
    );
};

export default CalendarTaskBody;
